<template>
  <v-card tile>
    <v-app-bar dark flat color="primary">
      <v-btn icon dark @click="cancel(user)">
        <v-icon>close</v-icon>
      </v-btn>
      <span class="title"
        >{{ isNew ? resx.labels.new : resx.labels.update }}
        {{ resx.labels.user }}</span
      >
    </v-app-bar>
    <form @submit.prevent="save()" novalidate v-if="user">
      <v-card-text>
        <v-container grid-list-xl>
          <v-row wrap>
            <v-col cols="12" md="6">
              <v-text-field
                :label="resx.labels.username"
                v-model="user.username"
                name="username"
                :data-vv-as="resx.labels.username"
                v-validate="`required|uniqueUsername:${user._id}`"
                :error-messages="errors.collect('username')"
                required
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                :append-icon="
                  passwordVisible ? 'visibility' : 'visibility_off'
                "
                @click:append="() => (passwordVisible = !passwordVisible)"
                data-vv-name="password"
                :data-vv-as="resx.labels.password"
                :label="resx.labels.password"
                :error-messages="errors.collect('password')"
                v-model="user.password"
                :type="!passwordVisible ? 'password' : 'text'"
                :v-validate="isNew ? 'required' : ''"
                :disabled="!isNew"
                required
              >
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                data-vv-name="firstname"
                :data-vv-as="resx.labels.firstname"
                :label="resx.labels.firstname"
                :error-messages="errors.collect('firstname')"
                v-model="user.firstname"
                v-validate="'required'"
                required
              >
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                data-vv-name="lastname"
                :data-vv-as="resx.labels.lastname"
                :label="resx.labels.lastname"
                :error-messages="errors.collect('lastname')"
                v-model="user.lastname"
                v-validate="'required'"
                required
              >
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                data-vv-name="email"
                :data-vv-as="resx.labels.email"
                :label="resx.labels.email"
                :error-messages="errors.collect('email')"
                v-model="user.email"
                v-validate="
                  `required|email|uniqueUserEmail:${user._id}`
                "
                type="email"
                required
              >
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="user.company"
                :label="resx.labels.company"
                :items="companies"
                item-text="name"
                item-value="_id"
                :no-data-text="resx.messages.noSelectorDataAvailable"
                :disabled="companies.length === 0"
              >
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                :items="roles"
                :label="resx.labels.role"
                item-text="name"
                item-value="code"
                v-model="user.roles[0]"
                v-if="user.roles"
                name="role"
                :error-messages="errors.collect('role')"
                :data-vv-as="resx.labels.role"
                v-validate="'required'"
                :disabled="!user.company"
                required
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-switch
                color="primary"
                :label="
                  `${resx.labels.user} ${
                    user.isActive
                      ? resx.labels.active
                      : resx.labels.inactive
                  }`
                "
                v-model="user.isActive"
              >
              </v-switch>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="cancel(user)">{{ resx.labels.cancel }}</v-btn>
        <v-btn class="primary" type="submit" :disabled="!isValidForm">{{
          resx.labels.ok
        }}</v-btn>
      </v-card-actions>
    </form>
  </v-card>
</template>

<script>
"use strict";

import companyService from "@/services/CompanyService";
import store from "@/store/store";
import userService from "@/services/UserService";
import { sprintf } from "sprintf-js";

const uniqueUserEmail = {
  getMessage() {
    return store.state.locale.resx.messages.uniqueEmail;
  },
  async validate(value, args) {
    const isValid = await userService.isValidEmail(args[0], value);
    return {
      valid: isValid
    };
  }
};

const uniqueUsername = {
  getMessage() {
    return store.state.locale.resx.messages.uniqueUsername;
  },
  async validate(value, args) {
    const isValid = await userService.isValidUsername(args[0], value);
    return {
      valid: isValid
    };
  }
};

export default {
  name: "edit-user",
  async mounted() {
    this.$validator.extend("uniqueUserEmail", uniqueUserEmail);
    this.$validator.extend("uniqueUsername", uniqueUsername);
    this.companies = await companyService.getCompanies();
    this.userChange(this.user);
  },
  asyncComputed: {
    async roles() {
      if (!this.user || !this.user.company) {
        return new Promise(resolve => {
          resolve([]);
        });
      }
      const vm = this;
      const allRoles = await userService.getRoles();
      const company = this.getCompanyById(
        this.user.company._id || this.user.company
      );
      const availableRoles = await allRoles.filter(role => {
        return role.isBuiltIn == company.isBuiltIn;
      });
      const findRole = availableRoles.find(role => {
        return role.code == vm.user.roles[0];
      });
      if (!findRole) {
        vm.user.roles = [];
        //patch: veevalidate not work very well with asyncComputed.
        vm.$nextTick(() => {
          this.$validator.validate("role");
        });
      }
      return availableRoles;
    }
  },
  computed: {
    isNew: function() {
      return !(this.user && this.user._id);
    }
  },
  data() {
    return {
      passwordVisible: false,
      companies: [],
      originalUser: {}
    };
  },
  methods: {
    async cancel(user) {
      if (JSON.stringify(this.originalUser) != JSON.stringify(this.user)) {
        await this.botConfirmDialog({
          title: `${
          this.resx.labels.edit
          } ${this.resx.labels.company.toLowerCase()}`,
          content: sprintf(
          this.resx.messages.noSavedChanges,
          user.name
          ),
          alertHint: ""
        });
        this.$emit("cancel");
        this.originalUser = {};
      } else {
        this.$emit("cancel");
        this.originalUser = {};
      }
    },
    async save() {
      let result = null;
      if (this.isNew) {
        result = await userService.addUser(this.user);
      } else {
        result = await userService.updateUser(this.user);
      }
      if (result) {
        this.$emit("saved", { isNew: this.isNew, user: result });
      }
      this.originalUser = {};
    },
    getCompanyById(id) {
      return this.companies.find(company => {
        return company._id == id;
      });
    },
    userChange(user) {
      if (typeof this.originalUser._id === "undefined") {
        this.originalUser = Object.assign({}, user);
      };
    }
  },
  props: {
    user: { type: Object }
  },
  watch: {
    user(val) {
      if (
        this.$store.state.auth.user.roles[0] ==
          this.$store.state.constants.roles.ADMIN &&
        val &&
        val.company == null
      ) {
        val.company = this.$store.state.auth.user.company._id;
      }
      this.userChange(val);
    },
  }
};

</script>
